import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import {
  canManageInstructorPublicProfile,
  canManageOrganizationPublicProfile,
  canViewPublicInstructorProfile,
  canViewPublicOrganizationProfile,
} from './platformAccess';

function normalizeString(value = '') {
  return String(value || '').trim();
}

function normalizeSlug(value = '') {
  return normalizeString(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function normalizeArray(values = []) {
  if (Array.isArray(values)) {
    return values
      .map((value) => normalizeString(value))
      .filter(Boolean);
  }

  return normalizeString(values)
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

async function loadPublicQueryRecords(db, { collectionName = '', filters = [] } = {}) {
  if (!collectionName) return [];

  try {
    const constraints = filters
      .filter((filter) => filter?.field)
      .map((filter) => where(filter.field, filter.operator || '==', filter.value));
    const snapshot = await getDocs(query(collection(db, collectionName), ...constraints));
    return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  } catch (error) {
    console.warn(`Public profile query failed for ${collectionName}:`, error);
    return [];
  }
}

export function normalizeOrganizationPublicProfile(record = {}, organization = {}) {
  const organizationId = record.organizationId || organization.organizationId || organization.id || '';
  const profileStatus = record.profileStatus || (record.isPublic ? 'published' : 'draft');
  const disciplines = normalizeArray(record.disciplines || organization.disciplines || []);
  const industries = normalizeArray(record.industries || organization.industries || organization.industry || []);

  return {
    organizationId,
    publicSlug: normalizeSlug(record.publicSlug || organization.publicSlug || organization.slug || organizationId),
    publicName: normalizeString(record.publicName || organization.publicName || organization.name || organization.organizationName || ''),
    publicDescription: normalizeString(record.publicDescription || ''),
    publicShortDescription: normalizeString(record.publicShortDescription || ''),
    logoUrl: normalizeString(record.logoUrl || organization.logoUrl || ''),
    coverImageUrl: normalizeString(record.coverImageUrl || ''),
    websiteUrl: normalizeString(record.websiteUrl || organization.websiteUrl || ''),
    locationLabel: normalizeString(record.locationLabel || organization.locationLabel || ''),
    disciplines,
    industries,
    languages: normalizeArray(record.languages || []),
    publicContactEmail: normalizeString(record.publicContactEmail || ''),
    publicContactUrl: normalizeString(record.publicContactUrl || ''),
    publicHighlights: normalizeArray(record.publicHighlights || []),
    featured: Boolean(record.featured),
    isPublic: Boolean(record.isPublic ?? profileStatus === 'published'),
    profileStatus,
  };
}

export function normalizeInstructorPublicProfile(record = {}, instructor = {}) {
  const profileStatus = record.profileStatus || (record.isPublic ? 'published' : 'draft');
  const organizationId = record.organizationId || instructor.organizationId || instructor.orgId || '';
  const displayName = normalizeString(record.displayName || instructor.displayName || instructor.name || '');

  return {
    userId: record.userId || instructor.userId || instructor.id || '',
    organizationId,
    publicSlug: normalizeSlug(record.publicSlug || instructor.publicSlug || `${displayName || 'instructor'}-${record.userId || instructor.id || ''}`),
    displayName,
    bio: normalizeString(record.bio || ''),
    shortBio: normalizeString(record.shortBio || ''),
    profileImageUrl: normalizeString(record.profileImageUrl || instructor.photoURL || ''),
    disciplines: normalizeArray(record.disciplines || instructor.disciplines || instructor.sport || []),
    specialties: normalizeArray(record.specialties || []),
    languages: normalizeArray(record.languages || []),
    certifications: normalizeArray(record.certifications || []),
    yearsExperience: Number(record.yearsExperience || 0) || 0,
    featured: Boolean(record.featured),
    isPublic: Boolean(record.isPublic ?? profileStatus === 'published'),
    profileStatus,
    publicTags: normalizeArray(record.publicTags || []),
  };
}

export async function getOrganizationPublicProfile(db, organizationId = '', organization = {}) {
  if (!organizationId) return normalizeOrganizationPublicProfile({}, organization);

  const snapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_PUBLIC_PROFILES, organizationId));
  return snapshot.exists()
    ? normalizeOrganizationPublicProfile({ id: snapshot.id, ...snapshot.data() }, organization)
    : normalizeOrganizationPublicProfile({ organizationId }, organization);
}

export async function getInstructorPublicProfile(db, instructorUserId = '', instructor = {}) {
  if (!instructorUserId) return normalizeInstructorPublicProfile({}, instructor);

  const snapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.INSTRUCTOR_PUBLIC_PROFILES, instructorUserId));
  return snapshot.exists()
    ? normalizeInstructorPublicProfile({ id: snapshot.id, ...snapshot.data() }, instructor)
    : normalizeInstructorPublicProfile({ userId: instructorUserId }, instructor);
}

export async function upsertOrganizationPublicProfile(
  db,
  {
    organizationId = '',
    organization = {},
    userData = {},
    profile = {},
  } = {}
) {
  if (!canManageOrganizationPublicProfile(userData, organizationId)) {
    throw new Error('Not authorized to manage organization public profile.');
  }

  const normalized = normalizeOrganizationPublicProfile(
    {
      ...profile,
      organizationId,
    },
    organization
  );

  await setDoc(
    doc(db, PLATFORM_COLLECTIONS.ORGANIZATION_PUBLIC_PROFILES, organizationId),
    {
      ...normalized,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return normalized;
}

export async function upsertInstructorPublicProfile(
  db,
  {
    currentUserId = '',
    userData = {},
    instructor = {},
    instructorUserId = '',
    isSelf = false,
    profile = {},
  } = {}
) {
    const normalized = normalizeInstructorPublicProfile(
      {
        ...profile,
        userId: instructorUserId || profile.userId || instructor.id || instructor.userId || '',
      },
      instructor
    );

  if (!canManageInstructorPublicProfile(userData, {
    currentUserId,
    instructorUserId: normalized.userId,
    organizationId: normalized.organizationId,
    isSelf,
  })) {
    throw new Error('Not authorized to manage instructor public profile.');
  }

  await setDoc(
    doc(db, PLATFORM_COLLECTIONS.INSTRUCTOR_PUBLIC_PROFILES, normalized.userId),
    {
      ...normalized,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return normalized;
}

export async function getOrganizationPublicProfileBySlug(db, publicSlug = '') {
  const records = await loadPublicQueryRecords(db, {
    collectionName: PLATFORM_COLLECTIONS.ORGANIZATION_PUBLIC_PROFILES,
    filters: [{ field: 'publicSlug', value: normalizeSlug(publicSlug) }],
  });

  const record = records.find(canViewPublicOrganizationProfile);
  return record ? normalizeOrganizationPublicProfile(record) : null;
}

export async function getInstructorPublicProfileBySlug(db, publicSlug = '') {
  const records = await loadPublicQueryRecords(db, {
    collectionName: PLATFORM_COLLECTIONS.INSTRUCTOR_PUBLIC_PROFILES,
    filters: [{ field: 'publicSlug', value: normalizeSlug(publicSlug) }],
  });

  const record = records.find(canViewPublicInstructorProfile);
  return record ? normalizeInstructorPublicProfile(record) : null;
}

export async function loadOrganizationInstructorPublicProfiles(db, organizationId = '') {
  const records = await loadPublicQueryRecords(db, {
    collectionName: PLATFORM_COLLECTIONS.INSTRUCTOR_PUBLIC_PROFILES,
    filters: [{ field: 'organizationId', value: organizationId }],
  });

  return records.map((record) => normalizeInstructorPublicProfile(record));
}

export async function getPublicOrganizations(db, filters = {}) {
  const records = await loadPublicQueryRecords(db, {
    collectionName: PLATFORM_COLLECTIONS.ORGANIZATION_PUBLIC_PROFILES,
    filters: [{ field: 'profileStatus', value: 'published' }],
  });

  return records
    .map((record) => normalizeOrganizationPublicProfile(record))
    .filter(canViewPublicOrganizationProfile)
    .filter((profile) => {
      if (filters.discipline && !profile.disciplines.includes(filters.discipline)) return false;
      if (filters.language && !profile.languages.includes(filters.language)) return false;
      if (filters.locationLabel && profile.locationLabel !== filters.locationLabel) return false;
      if (filters.featured && !profile.featured) return false;
      if (filters.searchTerm) {
        const haystack = [
          profile.publicName,
          profile.publicShortDescription,
          profile.locationLabel,
          ...(profile.disciplines || []),
          ...(profile.industries || []),
        ].join(' ').toLowerCase();
        if (!haystack.includes(String(filters.searchTerm).toLowerCase())) return false;
      }
      return true;
    });
}

export async function getPublicInstructors(db, filters = {}) {
  const records = await loadPublicQueryRecords(db, {
    collectionName: PLATFORM_COLLECTIONS.INSTRUCTOR_PUBLIC_PROFILES,
    filters: [{ field: 'profileStatus', value: 'published' }],
  });

  return records
    .map((record) => normalizeInstructorPublicProfile(record))
    .filter(canViewPublicInstructorProfile)
    .filter((profile) => {
      if (filters.organizationId && profile.organizationId !== filters.organizationId) return false;
      if (filters.discipline && !profile.disciplines.includes(filters.discipline)) return false;
      if (filters.language && !profile.languages.includes(filters.language)) return false;
      if (filters.featured && !profile.featured) return false;
      if (filters.searchTerm) {
        const haystack = [
          profile.displayName,
          profile.shortBio,
          ...(profile.disciplines || []),
          ...(profile.specialties || []),
          ...(profile.publicTags || []),
        ].join(' ').toLowerCase();
        if (!haystack.includes(String(filters.searchTerm).toLowerCase())) return false;
      }
      return true;
    });
}

export async function getFeaturedOrganizations(db) {
  return getPublicOrganizations(db, { featured: true });
}

export async function getFeaturedInstructors(db) {
  return getPublicInstructors(db, { featured: true });
}
